import { ASK_AICA_CONSTANTS } from './constants';
import { CitationSource } from './citationTypes';
import { AnswerScope } from '../../globalAskAica/answerScopeTypes';
import { ConversationListItem } from './types';

export type { ConversationListItem };

export interface AgentInput {
    /** Omitted entirely for a lender-agnostic ask — Athena branches on this key's
     * ABSENCE, so never send an empty string here. */
    lender_org_id?: string;
    product_id: string;
    product_name: string;
}

export interface SuggestedQuestion {
    id: string;
    text: string;
    flowKey?: string;
    agentInput?: AgentInput;
    display?: string; // Optional display text that can differ from the actual question text
    hint?: string; // Optional smaller-font hint shown after the title (e.g. expected duration)
}

export interface ReasoningStep {
    message: string;
    timestamp: number;
    type?: 'thinking' | 'searching' | 'error';
}

export interface Message {
    id: number;
    type: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    sources?: string[];
    flowKey?: string;
    agentInput?: AgentInput;
    suggestedQuestions?: SuggestedQuestion[];
    reasoning?: ReasoningStep[] | string | null;
    hasNewReasoning?: boolean;
    error?: boolean;
    errorMessage?: string;
    metadata?: {
        llm_used?: string;
        context_available?: boolean;
        timestamp?: number;
        step_type?: 'thinking' | 'searching' | 'error';
        error?: boolean;
        errorMessage?: string;
        /**
         * False when re-sending the same message is guaranteed to fail identically
         * (e.g. an over-length prompt), so the UI must not offer "Try Again".
         * ABSENT means retryable — Athena only sends it on the non-retryable path,
         * so every other error keeps its retry button.
         */
        retryable?: boolean;
        skills_invoked?: string[];
        file_url?: string;
        file_name?: string;
        file_type?: string;
        /** Citation sources from Athena (final chunk only). Do NOT touch legacy sources?: string[]. */
        sources?: CitationSource[];
        agg_tokens?: number;
        /** True when the backend confirms feedback was recorded. */
        feedback_ack?: boolean;
        /** PORTFOLIO-ONLY (RECUR-27889) — which companies the answer covered and
         * which written names it could not. Absent on in-lead turns by contract
         * (Athena frozen contract v4 §7), so it is optional here and the shared
         * MessagesContainer also gates its render on the surface. Declared on the
         * leads Message because that is the type the shared components receive;
         * the shape itself lives in an import-free leaf to avoid a cycle. */
        answer_scope?: AnswerScope;
    };

    /** True when this assistant message is a feedback ack (live: metadata.feedback_ack; reload: set by history map). */
    isFeedbackAck?: boolean;

    // For messages fetched from backend
    created_at?: string;
    input_text?: string;
    status?: string;
    query_source?: string;
}

export interface LibraryFile {
    fileUrl: string;
    fileName: string;
    fileType?: string;
    createdAt: string;
    source: string;
    mnemosyneDocId: string;
}

// UI-related actions
export function setAskAicaOpen(isOpen: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_ASK_AICA_OPEN,
        payload: isOpen,
    };
}

export function setAskAicaRightPanelMode(mode: 'suggestions' | 'reasoning') {
    return {
        type: ASK_AICA_CONSTANTS.SET_ASK_AICA_RIGHT_PANEL_MODE,
        payload: mode,
    };
}

// Action to add a message to the chat
export function addMessage(message: Message) {
    return {
        type: ASK_AICA_CONSTANTS.ADD_MESSAGE,
        payload: message,
    };
}

// Action to add a message to the chat. conversationId, when passed, is the
// conversation this page of history was fetched FOR (fetchChatHistory's own
// effectiveConversationId) — overlapping fetchChatHistory calls for different
// conversations can resolve out of order, so the reducer only applies this
// when conversationId still matches state.selectedConversationId. Omit it
// (undefined) for a caller with no single conversation to correlate against —
// the reducer then applies unconditionally, same as before this param existed.
export function initialiseMessageThread(messages: Message[], conversationId?: string) {
    return {
        type: ASK_AICA_CONSTANTS.INITIALISE_MESSAGE_THREAD,
        payload: { messages, conversationId },
    };
}

// Action to update a message (used for streaming updates)
export function updateMessage(id: number, content: string, metadata?: any) {
    return {
        type: ASK_AICA_CONSTANTS.UPDATE_MESSAGE,
        payload: { id, content, metadata },
    };
}

// Action to set loading state
export function setIsLoading(isLoading: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_IS_LOADING,
        payload: isLoading,
    };
}

// Action to set clearing chat state
export function setIsClearingChat(isClearingChat: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_IS_CLEARING_CHAT,
        payload: isClearingChat,
    };
}

// Action to set loading state
export function setIsLoadingChatHistory(isLoading: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_IS_LOADING_CHAT_HISTORY,
        payload: isLoading,
    };
}

// Action to clear all messages
export function clearMessages(orgId?: string) {
    return {
        type: ASK_AICA_CONSTANTS.CLEAR_MESSAGES,
        payload: orgId,
    };
}

// Action to set an error
export function setError(error: string | null) {
    return {
        type: ASK_AICA_CONSTANTS.SET_ERROR,
        payload: error,
    };
}

// Action to set reasoning for a message
export function setReasoning(messageId: number, reasoning: ReasoningStep[], hasNewReasoning: boolean = false) {
    return {
        type: ASK_AICA_CONSTANTS.SET_REASONING,
        payload: { messageId, reasoning, hasNewReasoning },
    };
}

// Action to reset reasoning
export function resetReasoning() {
    return {
        type: ASK_AICA_CONSTANTS.RESET_REASONING,
    };
}

// Action to update reasoning for a message
export function updateReasoning(messageId: number, reasoning: string | null, hasNewReasoning: boolean = false) {
    return {
        type: ASK_AICA_CONSTANTS.UPDATE_REASONING,
        payload: { messageId, reasoning, hasNewReasoning },
    };
}

// Action to set global suggested questions (replaces all). conversationId is
// the same overlapping-fetch correlation as initialiseMessageThread above —
// see that doc comment. Omit it for a caller (e.g. a live turn's own
// suggestions) that isn't subject to the same conversation-switch race.
export function setGlobalSuggestedQuestions(questions: SuggestedQuestion[], conversationId?: string) {
    return {
        type: ASK_AICA_CONSTANTS.SET_GLOBAL_SUGGESTED_QUESTIONS,
        payload: { questions, conversationId },
    };
}

// Action to update global suggested questions (adds to existing)
export function updateGlobalSuggestedQuestions(questions: SuggestedQuestion[]) {
    return {
        type: ASK_AICA_CONSTANTS.UPDATE_GLOBAL_SUGGESTED_QUESTIONS,
        payload: questions,
    };
}

// Action to set the Library files loading state
export function setLibraryLoading(isLoading: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_LIBRARY_LOADING,
        payload: isLoading,
    };
}

// Action to set the Library files (replaces all)
export function setLibraryFiles(files: LibraryFile[]) {
    return {
        type: ASK_AICA_CONSTANTS.SET_LIBRARY_FILES,
        payload: files,
    };
}

// Action to prepend newly generated file(s) to the Library list — used to reflect a
// just-generated file immediately, without refetching the full list from Polus.
export function addLibraryFiles(files: LibraryFile[]) {
    return {
        type: ASK_AICA_CONSTANTS.ADD_LIBRARY_FILES,
        payload: files,
    };
}

export function setConversations(conversations: ConversationListItem[]) {
    return {
        type: ASK_AICA_CONSTANTS.SET_CONVERSATIONS,
        payload: conversations,
    };
}

export function setLoadingConversations(isLoading: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_LOADING_CONVERSATIONS,
        payload: isLoading,
    };
}

export function setSelectedConversation(conversationId: string | null) {
    return {
        type: ASK_AICA_CONSTANTS.SET_SELECTED_CONVERSATION,
        payload: conversationId,
    };
}

// Resets chat view (messages, reasoning, suggestedQuestions, pagination,
// isLoadingChatHistory) without touching conversations/selectedConversationId/
// library/uiState. Dispatch BEFORE setSelectedConversation on a conversation
// switch so initMessageThread starts from a clean slate.
export function resetChatView() {
    return {
        type: ASK_AICA_CONSTANTS.RESET_CHAT_VIEW,
    };
}

// True from New Chat click until the first chunk of the next turn confirms a
// conversation id — the FE's only representation of "New Chat clicked, first
// send pending" (D1/D2). Cleared by setSelectedConversation (reducer.ts).
export function setPendingNewConversation(pending: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_PENDING_NEW_CONVERSATION,
        payload: pending,
    };
}

// Mirrors the chat-history response's own `is_deleted` field (Athena's
// share-view contract — GET /chat/conversation/history?conversation_id=...
// serves a soft-deleted conversation's history with is_deleted:true rather
// than 404ing) for whichever conversation is currently loaded. Dispatched from
// useAskAica's fetchChatHistory on every response that carries the field.
//
// conversationId is the id that was ACTUALLY REQUESTED for this response (its
// effectiveConversationId at call time) — two fetchChatHistory calls for
// different conversations can overlap in flight, so the reducer needs this to
// discard a late response for a conversation the user has since navigated
// away from, rather than clobbering selectedConversationIsDeleted for
// whichever conversation is CURRENTLY selected by the time the response lands.
export function setSelectedConversationIsDeleted(conversationId: string | undefined, isDeleted: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_SELECTED_CONVERSATION_IS_DELETED,
        payload: { conversationId, isDeleted },
    };
}
